import { produce } from 'immer';
import { useCallback, useEffect, useRef, useState, } from 'react';
import { defaultDropAnimationSideEffects } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { transform } from 'ol/proj';
const multipleEventHandler = (...fns) => (event) => fns.forEach((fn) => fn(event));
const formatPrice = (price) => {
    const roundedPrice = Math.round((price / 1000)) * 1000;
    if (roundedPrice < 1000)
        return price.toString();
    if (roundedPrice < 1000000)
        return `${(price / 1000).toFixed(1).replace('.0', '')}K`;
    return `${(roundedPrice / 1000000).toFixed(1).replace('.0', '')}M`;
};
// меняем местами цену и валюту если арабский язык
const formatPriceForLang = (price, isRtl, measurement) => `${isRtl ? measurement + ' ' : ''}${price}${isRtl ? '' : ' ' + measurement}`;
const useToggle = (defaultState = false) => {
    const [state, setState] = useState(defaultState);
    const toggle = useCallback(() => setState((a) => !a), []);
    const force = useCallback((a) => setState(a), []);
    return [state, toggle, force];
};
const getDaysToDate = (from) => {
    if (!from)
        return undefined;
    const now = dayjs(dayjs().utc().format('YYYY-MM-DD HH:mm'));
    const expiredAt = dayjs(from).utc();
    const difference = expiredAt.diff(now, 'day');
    return difference;
};
const useDebounce = (value, delay) => {
    const [state, setState] = useState(value);
    useEffect(() => {
        const timeoutId = setTimeout(() => setState(value), delay);
        return () => clearTimeout(timeoutId);
    }, [value, delay]);
    return state;
};
const debounce = (delay) => {
    let timerId;
    return (callback) => {
        clearTimeout(timerId);
        timerId = setTimeout(callback, delay);
    };
};
const useDidMountEffect = (callback, deps) => {
    const didMount = useRef(false);
    useEffect(() => {
        if (!didMount.current)
            didMount.current = true;
        else
            return callback();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
};
const squareRatios = {
    m2: 0.092903,
    ft2: 1,
};
export const squareView = {
    m2: 'm²',
    ft2: 'ft²',
};
function convertSquareUnit(square, unit1, unit2, asNumber = false, withUnit = true) {
    if (!square || !unit1 || !unit2)
        return undefined;
    const convertedValue = (square * squareRatios[unit2] / squareRatios[unit1]);
    if (asNumber)
        return convertedValue;
    if (!withUnit)
        return Number(convertedValue.toFixed(2)).toLocaleString('en-US');
    return `${Number(convertedValue.toFixed(2)).toLocaleString('en-US')} ${squareView[unit2]}`;
}
function countryCurrencyToCurrency(price, unit, exchange, asNumber = false, withCurrency = true) {
    if (!price || !unit || !exchange)
        return undefined;
    const convertedValue = Number((price * exchange?.[unit]).toFixed(2));
    if (asNumber)
        return convertedValue;
    if (!withCurrency)
        return convertedValue.toLocaleString('en-US');
    return `${convertedValue.toLocaleString('en-US')} ${unit}`;
}
function currencyToCountryCurrency(countryCurrency, price, unit, exchange, asNumber = false) {
    if (!price || !unit || !exchange)
        return undefined;
    if (asNumber)
        return Number((Number(price) / exchange?.[unit]).toFixed(2));
    return `${Number((Number(price) / exchange?.[unit]).toFixed(2)).toLocaleString('en-US')} ${countryCurrency}`;
}
const formatHandover = (date) => {
    if (!date)
        return;
    const year = Number(date.split('-')[0]);
    const month = Number(date.split('-')[1]);
    return `Q${Math.ceil((month / 12) * 4)}/${year}`;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const get = (obj, path) => {
    if (!path)
        return obj;
    const child = path.split('.');
    return get(obj?.[child[0]], child.slice(1).join('.'));
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const set = (val, path, obj) => {
    if (!path)
        return obj;
    const child = path.split('.');
    const key = child[0];
    return child.length === 1
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? produce(obj, (draft) => {
            draft[key] = val;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        : produce(obj, (draft) => {
            draft[key] = set(val, child.slice(1).join('.'), draft[key]);
        });
};
const sortBy = (arr, path, direction = true) => ([...arr].sort((a, b) => (get(a, path) > get(b, path)
    ? (direction ? 1 : -1)
    : get(a, path) < get(b, path)
        ? (direction ? -1 : 1)
        : 0)));
const toggleArrayItem = (item, array) => {
    if (array.includes(item))
        return array.filter((arrayItem) => arrayItem !== item);
    return [...array, item];
};
const moveItemToArray = (arrFrom, arrTo, indexFrom, indexTo) => {
    const sourceClone = [...arrFrom];
    const destClone = [...arrTo];
    const [removed] = destClone.splice(indexFrom, 1);
    sourceClone.splice(indexTo, 0, removed);
    return [sourceClone, destClone];
};
const copyToBuffer = (value) => {
    window.navigator.clipboard.writeText(value);
};
const useMemoizedState = (value) => {
    const [state, setState] = useState(value);
    useEffect(() => {
        setState(value);
    }, [value]);
    return [state, setState];
};
const counter = (val) => {
    let i = 0;
    return () => {
        i = i === val ? 0 : i + 1;
        return i;
    };
};
function isEmpty(data) {
    return Array.isArray(data) ? !data?.length : !Object.keys(data || {}).length;
}
// Runtime anchor, substituted by scripts/40-envsubst-js.sh at container start (same scheme as API_BASE_URL).
const RUNTIME_REVERSE_PROXY_URL_ANCHOR = '${REVERSE_PROXY_URL}';
const isReverseProxyAnchorUnsubstituted = /\$\{\s*REVERSE_PROXY_URL\s*\}/.test(RUNTIME_REVERSE_PROXY_URL_ANCHOR);
const reverseProxyUrl = isReverseProxyAnchorUnsubstituted ? 'https://reverse.geniemap.net' : RUNTIME_REVERSE_PROXY_URL_ANCHOR;
const toProxy = (url) => url?.replace('https://', `${reverseProxyUrl}/`);
const toLonLat = (geometry) => {
    if (!Array.isArray(geometry[0])) {
        const [x, y] = transform(geometry, 'EPSG:3857', 'EPSG:4326');
        return [+x.toFixed(6), +y.toFixed(6)];
    }
    return geometry.map(toLonLat);
};
const fromLonLat = (geometry) => {
    if (!Array.isArray(geometry[0])) {
        const [x, y] = transform(geometry, 'EPSG:4326', 'EPSG:3857');
        return [+x.toFixed(6), +y.toFixed(6)];
    }
    return geometry.map(fromLonLat);
};
const parseQuery = (url) => url
    .replace(/(?:.*?)(\?)(.*)/gm, (_, b, c) => `${b}${c}`)
    .split(/[?|&]([^=&]+)=([^&]*)/gm)
    .filter(Boolean)
    .reduce((acc, item, i, arr) => (arr[i * 2] ? {
    ...acc,
    [arr[i * 2]]: arr[i * 2 + 1],
} : acc), {});
const downloadFile = (file, name) => {
    const objUrl = window.URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = objUrl;
    link.download = name;
    link.click();
    setTimeout(() => {
        window.URL.revokeObjectURL(objUrl);
    }, 250);
};
const defaultDropAnimationConfig = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {},
            dragOverlay: {
                display: 'none',
            },
        },
    }),
};
const sortUnitsBy = (units, path, order, reverse = false) => {
    const orderMap = new Map(order.map((item, index) => [item, index]));
    return [...units].sort((a, b) => {
        const indexA = orderMap.has(get(a, path)) ? orderMap.get(get(a, path)) : Infinity;
        const indexB = orderMap.has(get(b, path)) ? orderMap.get(get(b, path)) : Infinity;
        return reverse ? indexB - indexA : indexA - indexB;
    });
};
const sortUnitsByFloor = (units, reverse = false) => {
    const orderMap = {
        '': 0,
        null: 0,
        undefined: 0,
        low: 1,
        medium: 2,
        high: 3,
    };
    function getGroupIndex(value) {
        const val = String(value);
        if (Object.prototype.hasOwnProperty.call(orderMap, val))
            return orderMap[val];
        if (/[a-zA-ZА-Яа-я]/.test(val))
            return 4;
        if (/^\d+$/.test(val))
            return 5;
        return 6;
    }
    const sorted = [...units].sort((a, b) => {
        const aFloor = a.resaleInfo?.floor || a.floor;
        const bFloor = b.resaleInfo?.floor || b.floor;
        const groupA = getGroupIndex(aFloor);
        const groupB = getGroupIndex(bFloor);
        if (groupA !== groupB)
            return reverse ? groupB - groupA : groupA - groupB;
        if (groupA === 5)
            return reverse ? Number(bFloor) - Number(aFloor) : Number(aFloor) - Number(bFloor);
        return reverse ? bFloor?.localeCompare(aFloor) : aFloor?.localeCompare(bFloor);
    });
    return sorted;
};
const truncateFilename = (filename, maxBaseLength = 30) => {
    if (typeof filename !== 'string')
        return '';
    const lastDotIndex = filename.lastIndexOf('.');
    if (lastDotIndex === -1 || lastDotIndex === 0) {
        return filename.length > maxBaseLength ? `${filename.slice(0, maxBaseLength)}...` : filename;
    }
    const base = filename.slice(0, lastDotIndex);
    const ext = filename.slice(lastDotIndex + 1);
    if (base.length <= maxBaseLength) {
        return `${base}.${ext}`;
    }
    return `${base.slice(0, maxBaseLength)}...${ext}`;
};
const readFileAsBuffer = async (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file); // Возвращает в onload ArrayBuffer
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Ошибка чтения файла: ${file.name}`));
});
const getSHA256Hash = async (buffer) => {
    const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
};
const areFilesIdentical = async (...files) => {
    if (files.length < 2)
        return true;
    if (files.includes(undefined))
        return false;
    const hashPromises = files.map(async (file) => {
        const buffer = await readFileAsBuffer(file);
        return getSHA256Hash(buffer);
    });
    const hashes = await Promise.all(hashPromises);
    return hashes.every((hash) => hash === hashes[0]);
};
const emptyFn = () => { };
const isKeyOfObj = (key, obj) => key in obj;
// !!!!!!!!!! ВАЖНО, использовать с исключительной осторожностью, только в случае, когда других вариантов нет. Очень ресурсоёмкая операция !!!!!!!!!!
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEqual = (obj1, obj2) => {
    if (obj1 === obj2)
        return true;
    if (obj1 == null || obj2 == null || typeof obj1 !== 'object' || typeof obj2 !== 'object')
        return obj1 === obj2;
    const isArray1 = Array.isArray(obj1);
    const isArray2 = Array.isArray(obj2);
    if (isArray1 !== isArray2)
        return false;
    if (isArray1) {
        if (obj1.length !== obj2.length)
            return false;
        return obj1.every((item, index) => isEqual(item, obj2[index]));
    }
    if (obj1 instanceof Date && obj2 instanceof Date)
        return obj1.getTime() === obj2.getTime();
    if (obj1 instanceof RegExp && obj2 instanceof RegExp)
        return obj1.toString() === obj2.toString();
    if (obj1 instanceof Map && obj2 instanceof Map) {
        if (obj1.size !== obj2.size)
            return false;
        for (const [key, value] of obj1) {
            if (!obj2.has(key) || !isEqual(value, obj2.get(key)))
                return false;
        }
        return true;
    }
    if (obj1 instanceof Set && obj2 instanceof Set) {
        if (obj1.size !== obj2.size)
            return false;
        const arr1 = Array.from(obj1);
        const arr2 = Array.from(obj2);
        return arr1.every((item) => arr2.some((item2) => isEqual(item, item2)));
    }
    const keys1 = [...Object.keys(obj1), ...Object.getOwnPropertySymbols(obj1)];
    const keys2 = [...Object.keys(obj2), ...Object.getOwnPropertySymbols(obj2)];
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        if (!obj2.hasOwnProperty(key)) { // eslint-disable-line
            return false;
        }
        if (!isEqual(obj1[key], obj2[key])) {
            return false;
        }
    }
    return true;
};
export { multipleEventHandler, formatPrice, formatPriceForLang, useToggle, getDaysToDate, useDebounce, debounce, useDidMountEffect, convertSquareUnit, countryCurrencyToCurrency, currencyToCountryCurrency, formatHandover, get, set, sortBy, toggleArrayItem, moveItemToArray, copyToBuffer, useMemoizedState, counter, isEmpty, toProxy, toLonLat, fromLonLat, parseQuery, downloadFile, defaultDropAnimationConfig, sortUnitsBy, sortUnitsByFloor, truncateFilename, readFileAsBuffer, getSHA256Hash, areFilesIdentical, emptyFn, isKeyOfObj, isEqual, };
